import { useEffect, useState } from 'react'

import { Dashboard } from '../../models/Dashboard'
import { Lesson } from '../../models/Lesson'
import { RecordFactory } from '../../models/RecordFactory'
import { getViewTitleFromView } from '../../utils/dashboard'
import { ComponentData } from '../../utils/typing'
import { Icon } from '../Icon'
import { PocketComponent, PocketComponentProps, usePSComponent } from '../PocketComponent'
import { Spinner } from '../Spinner'
import Label from '../app/design/Label'
import Paragraph from '../app/design/Paragraph'
import {
    ClickableTableRow,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../app/design/Table'
import { DashboardSkeleton } from '../common/skeletons/DashboardSkeleton'
import { EmptyDashboardLayout } from './EmptyDashboardLayout'
import { KeyMetric } from './KeyMetric'
import { ProListTableCell } from './ProListTableCell'

type Props = {
    classes: Lesson[]
    view: number
    dashboard?: Dashboard
    searchText: string
    isLoading: boolean
}

export function CapacityStringCell({ lesson }: { lesson: Lesson }) {
    const [capacity, setCapacity] = useState<string>('')
    useEffect(() => {
        ;(async () => {
            setCapacity(await lesson.capacityString())
        })()
    }, [])

    return (
        <Paragraph size="sm" className="">
            {capacity ? capacity : <Spinner size={24} />}
        </Paragraph>
    )
}

const viewWillAppear = async (base: PocketComponent<Props>) => {
    return await Promise.all(
        base.props.classes.map((c) => RecordFactory.getInstance().fetchLesson(c.id!))
    )
}

function render({ base }: PocketComponentProps<ComponentData<typeof viewWillAppear>, Props>) {
    const { classes, view, dashboard, searchText, isLoading } = base.props

    if (isLoading) {
        return <DashboardSkeleton />
    }

    return (
        <div className="flex flex-col gap-6">
            <KeyMetric
                metrics={[
                    {
                        title: `${getViewTitleFromView(dashboard, view)} ${
                            classes.length === 1 ? `class` : `classes`
                        }`,
                        value: classes.length.toString(),
                    },
                ]}
            />
            <Table>
                <TableHeader>
                    <TableRow location="head">
                        <TableHead className="font-normal">Title</TableHead>
                        <TableHead className="font-normal">Capacity</TableHead>
                        <TableHead className="font-normal">Instructor</TableHead>
                        <TableHead className="font-normal">
                            {view === 2 ? 'Cancelled' : 'Scheduled'}
                        </TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {classes.map((c) => {
                        return (
                            <ClickableTableRow key={c.id} href={`/lessons/${c.id}`}>
                                <TableCell>
                                    {c.title ? (
                                        <div className="flex items-center gap-4">
                                            <Icon
                                                icon="student.small"
                                                shouldInheritColor
                                                className="text-primary-700 size-9"
                                                background="round"
                                            />
                                            <Label size="sm">{c.title}</Label>
                                        </div>
                                    ) : (
                                        <Paragraph size="sm">-</Paragraph>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <CapacityStringCell lesson={c} />
                                </TableCell>
                                <TableCell>
                                    {c?.employee ? (
                                        <ProListTableCell pros={[c?.employee]} size="xs" />
                                    ) : (
                                        <Paragraph size="sm">-</Paragraph>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <Paragraph size="sm">
                                        {view === 2
                                            ? c.cancelledDate?.format('MMM D, YYYY')
                                            : c.date?.format('MMM D, YYYY')}
                                    </Paragraph>
                                </TableCell>
                            </ClickableTableRow>
                        )
                    })}
                    {classes.length === 0 && (
                        <TableCell colSpan={10}>
                            <EmptyDashboardLayout dashboard={dashboard} searchtext={searchText} />
                        </TableCell>
                    )}
                </TableBody>
            </Table>
        </div>
    )
}

export const ClassesDashboard = usePSComponent(render, viewWillAppear)
